// Feature profiles for the best-selling blades — one editorial page per profile
// at /machetes/<slug>. Content is owner-approved product knowledge; keep claims
// consistent with constants.ts specs and the Incolma catalog (no invented refs).

export interface ProfileSection {
  title: string;
  body: string;
}

export interface FeatureProfile {
  slug: string;
  name: string;
  label: string; // mono eyebrow, e.g. 'Saw + machete · 18"'
  tagline: string;
  image: string;
  productIds: string[]; // ids in PRODUCTS (first id drives price/specs display)
  seoDescription: string;
  intro: string[];
  useCases: ProfileSection[];
  care: ProfileSection[];
  safety: ProfileSection[];
}

export const FEATURE_PROFILES: FeatureProfile[] = [
  {
    slug: 'tranzidor-45',
    name: 'Tranzidor 45',
    label: 'Saw + machete · 18"',
    tagline: 'One tool, two jobs — a sharpened machete edge on one side, serrated saw teeth along the spine.',
    image: '/images/products/tranzidor-45.webp',
    productIds: ['M'],
    seoDescription:
      'Tranzidor 45 profile — the dual-function saw + machete field tool. Best uses, care and maintenance, and safe-handling tips from Canada\'s exclusive Incolma distributor.',
    intro: [
      'The Tranzidor 45 is the tool you reach for when the job keeps changing. The working side is a full 18-inch machete edge for clearing, chopping, and limbing; flip it over and the spine carries a row of serrated saw teeth for branches and small timber too thick to chop cleanly.',
      'Black-finished blade, high-visibility yellow ergonomic handle, full-tang Incolma construction. For trail crews, cottagers, and anyone who would otherwise carry a machete and a folding saw, this replaces both.',
    ],
    useCases: [
      {
        title: 'Trail and bush camp',
        body: 'Clear the path with the edge, then saw limbs for shelter poles or firewood without swapping tools. The 18-inch blade gives real reach, and the saw handles anything past wrist-thick where chopping gets wasteful.',
      },
      {
        title: 'Storm and yard cleanup',
        body: 'Fallen limbs rarely come in one size. Chop the whippy growth, saw the structural branches. The high-visibility handle is easy to track in a brush pile when you set it down.',
      },
      {
        title: 'Cottage, acreage, and trapline',
        body: 'When space in the truck box or pack is tight, one dual-function tool covers fence-line brush, overhanging branches, and kindling prep for the season.',
      },
      {
        title: 'Woody saplings and regrowth',
        body: 'Young hardwood regrowth is springy — it absorbs chops. Score it with the edge and finish with the saw for a clean, low-effort cut.',
      },
    ],
    care: [
      {
        title: 'Clean the saw teeth after cutting live wood',
        body: 'Sap and resin gum the teeth and invite rust in the gullets. Wipe the saw side down with an oily rag after use, and pick packed debris out of the teeth with a brass brush — not your fingers.',
      },
      {
        title: 'Oil the whole blade, finish or not',
        body: 'The black finish slows corrosion on the flats, but every sharpened edge and every saw tooth is bare carbon steel. A light coat of oil (mineral, 3-in-1, or camellia) after each outing keeps rust off.',
      },
      {
        title: 'Sharpen the machete edge with a mill file',
        body: 'Hold roughly a 20–25° angle and file into the edge with smooth, full-length strokes. A few passes per side after each heavy day is easier than a full re-grind later. Leave the saw teeth alone unless they truly dull — they cut far longer than the plain edge between sharpenings.',
      },
      {
        title: 'Store it dry and sheathed',
        body: 'Never put it away wet, and never store it in a leather sheath long-term — leather holds moisture. Hang it or shelve it somewhere dry with a film of oil on the steel.',
      },
    ],
    safety: [
      {
        title: 'Never grip the spine',
        body: 'On most machetes the spine is a safe place to press with your off-hand for push cuts. Not this one — the spine is a saw. Keep your off-hand on the handle side of the tool, always.',
      },
      {
        title: 'Use a sheath that covers both sides',
        body: 'The Tranzidor cuts in two directions. Transport and store it in a rigid or double-welt sheath that shields the saw teeth as well as the edge — and wrap it for vehicle transport as required in your province.',
      },
      {
        title: 'Saw away from your body',
        body: 'Brace the work, not the blade. Saw with strokes that travel away from your legs and off-hand, and kneel to the side of the cut line, never over it.',
      },
      {
        title: 'Chop with a clear follow-through',
        body: 'Before any swing, check a full arm-plus-blade radius — about three metres — for people, pets, and snag hazards overhead. Angle chops away from your legs so a glancing blow travels into open ground.',
      },
    ],
  },
  {
    slug: 'incolma-702w',
    name: 'Incolma 702W',
    label: 'Tres Canales · 20", 22"',
    tagline: 'The iconic three-fuller Latin machete — the everyday workhorse for brush, trail, and farm.',
    image: '/images/products/M1.webp',
    productIds: ['A1', 'A2'],
    seoDescription:
      'Incolma 702W (Tres Canales) profile — the three-fuller Latin workhorse machete. Best uses, care and maintenance, and safe-handling tips from Canada\'s exclusive Incolma distributor.',
    intro: [
      'The 702W is the blade most people picture when they picture a machete. Its straight Latin working profile carries three longitudinal fullers — the canales — that take weight out of the blade without taking out strength, so it swings fast and keeps swinging all day.',
      'We stock it in 20-inch and 22-inch lengths with a riveted hardwood handle. Drop-forged carbon steel, full tang, and a profile that has been a Colombian field standard for generations.',
    ],
    useCases: [
      {
        title: 'Brush and trail clearing',
        body: 'This is the 702W\'s home turf. The straight edge and light swing weight make quick work of grasses, ferns, canes, and soft brush — ideal for opening trails, sightlines, and fence rows.',
      },
      {
        title: 'Farm and acreage general work',
        body: 'From clearing around outbuildings to topping cover crops, the 702W is the one-blade answer for property upkeep. The 22-inch version covers more ground per stroke; the 20-inch is nimbler in tight growth.',
      },
      {
        title: 'Garden overgrowth',
        body: 'Season-end raspberry canes, ornamental grasses, cedar whips along the lot line — the thin, fast edge slices soft- and medium-stem growth that would fold under a heavier chopping blade.',
      },
      {
        title: 'Long sessions without fatigue',
        body: 'The fullers exist for a reason: less steel means less weight at the same stiffness. If you swing for hours rather than minutes, this profile is measurably easier on the shoulder than a belly-heavy blade.',
      },
    ],
    care: [
      {
        title: 'Wipe and oil after every use',
        body: 'This is high-carbon steel, not stainless — left wet, it will spot overnight. Wipe the blade clean and dry, then apply a light coat of oil down both faces and into the fullers, where moisture likes to sit.',
      },
      {
        title: 'Touch up with a mill file at 20–25°',
        body: 'A Latin machete wants a working edge, not a razor polish. File into the edge at a consistent shallow angle, alternating sides, until you raise and remove a light burr. Two minutes of filing beats an hour of grinding.',
      },
      {
        title: 'Feed the hardwood handle',
        body: 'A few drops of boiled linseed oil rubbed into the wood once or twice a season keeps the handle from drying and checking. Check the rivets stay snug — any movement in the grip is a reason to stop and fix, not push on.',
      },
      {
        title: 'Let the patina come',
        body: 'Carbon steel darkens with use. That grey-blue patina is a protective layer, not damage — leave it. Only bright orange rust needs removing, with fine steel wool and oil.',
      },
    ],
    safety: [
      {
        title: 'Respect the arc',
        body: 'A 22-inch blade at the end of your arm sweeps a big circle. Keep everyone — and dogs — at least three metres away, and check overhead for branches that could deflect a swing before it starts.',
      },
      {
        title: 'Cut away, angle away',
        body: 'Swing across your body and away from your legs, with chops angled so the blade travels into open ground on follow-through. Never clear growth between your feet.',
      },
      {
        title: 'Mind the off-hand',
        body: 'The most common machete injury is to the hand holding the brush. If you must hold material to cut it, grip it well above the cut line and keep the blade\'s exit path clear of your arm.',
      },
      {
        title: 'Carry and transport it covered',
        body: 'Walk with the blade sheathed or held at your side, tip down, edge back. In a vehicle, transport it wrapped or sheathed and stowed — out of reach and out of sight.',
      },
    ],
  },
  {
    slug: 'incolma-1820',
    name: 'Incolma 1820',
    label: 'Compact agricultural · 14.5"',
    tagline: 'A compact agricultural workhorse with a moderate belly curve — control over reach.',
    image: '/images/products/M4.webp',
    productIds: ['E'],
    seoDescription:
      'Incolma 1820 profile — the compact 14.5-inch agricultural machete. Best uses, care and maintenance, and safe-handling tips from Canada\'s exclusive Incolma distributor.',
    intro: [
      'Not every job wants two feet of steel. The 1820 puts a moderate belly curve on a compact 14.5-inch blade, trading reach for control — enough tip mass to chop with authority, short enough to work close without the swing getting away from you.',
      'Riveted hardwood handle, drop-forged carbon steel, full tang. It is the blade we recommend for orchards, smallholds, and anyone working nearer to their own hands than to the horizon.',
    ],
    useCases: [
      {
        title: 'Orchard and fruit-tree work',
        body: 'Limbing suckers, clearing water sprouts, cutting back around trunks — the short blade maneuvers inside a canopy where a full-size machete can\'t swing, and the belly concentrates force for clean cuts through live wood.',
      },
      {
        title: 'Smallhold and homestead daily tasks',
        body: 'Opening feed bags, cutting twine and stakes, splitting kindling, harvesting squash and brassicas — the 1820 earns a permanent spot by the barn door as the do-everything blade.',
      },
      {
        title: 'Close-quarters garden clearing',
        body: 'In raised beds and tight borders, reach is a liability. The compact blade lets you cut precisely next to plants you want to keep.',
      },
      {
        title: 'The pack blade',
        body: 'At 14.5 inches and well under two pounds, it rides on a pack or in a canoe barrel without snagging everything — a genuinely packable camp blade for limbing and kindling.',
      },
    ],
    care: [
      {
        title: 'The carbon-steel routine',
        body: 'Same rule as every Incolma blade: wipe it clean, dry it fully, oil it lightly after each use. Small blades get forgotten in buckets and truck beds more often than big ones — don\'t let it sit wet.',
      },
      {
        title: 'File with the curve',
        body: 'The belly means the edge isn\'t a straight line. Work the mill file in sections, following the curve so the file stays flat on the bevel through the sweep of the belly — that\'s where this blade does its chopping, so keep that section keenest.',
      },
      {
        title: 'Handle and rivets',
        body: 'Rub boiled linseed oil into the hardwood a couple of times a season and confirm the rivets are tight. Orchard work means sap — wipe the handle too, or the grip gets tacky and unpredictable.',
      },
    ],
    safety: [
      {
        title: 'Short blade, same force',
        body: 'A compact machete invites working close — which puts your off-hand and knee nearer the edge. Keep the off-hand behind the cutting plane and never anchor material directly below the cut.',
      },
      {
        title: 'Watch the knee line',
        body: 'Most 1820 work happens low: beds, trunks, kindling. Kneel beside the work rather than crouching over it, and keep chops angled so a glance-off travels into soil, not shin.',
      },
      {
        title: 'Control beats speed',
        body: 'The 1820 rewards deliberate, placed cuts. If you find yourself swinging hard and fast at something, that something probably wants the saw or a bigger blade — switch tools rather than forcing it.',
      },
      {
        title: 'Same carry rules as the big blades',
        body: 'Compact doesn\'t mean casual: sheathed when not in use, tip down when walking, wrapped and stowed for transport, and 18+ with ID on every delivery — same as everything we ship.',
      },
    ],
  },
];

export const getFeatureProfile = (slug: string): FeatureProfile | undefined =>
  FEATURE_PROFILES.find((p) => p.slug === slug);
